<template>
  <transition
    name="modal"
    appear
    @enter="onEnter"
    @after-enter="onAfterEnter"
    @leave="onLeave"
  >
    <div
      v-if="visible"
      key="modal"
      ref="modal"
      class="modal"
      :class="{ scrollable: scrollable, 'modal_bottom-sheet-mode': bottomSheetMode, 'modal_full-screen': fullScreen }"
    >
      <div
        ref="content"
        v-click-outside="closeByOutside"
        class="modal-content"
      >
        <div
          v-if="title"
          class="modal__title"
        >
          {{ title }}
        </div>
        <slot />
        <div
          v-if="!noClose"
          ref="closeButton"
          class="close-button"
          :class="{ 'close-button_fixed': closeFixed }"
          @click.stop="close"
        >
          <f-sprite-icon :icon-link="$options.icons.IconClose" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { mapGetters, mapMutations } from 'vuex';

import IconClose from '@/assets/icons/close.svg';

export default {
  name: 'Modal-component',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    scrollable: {
      type: Boolean,
      default: false,
    },

    backgroundMode: { // отключает блокировку bodyScroll и click-outside(можно открыть фоном)
      type: Boolean,
      default: false,
    },

    bottomSheetMode: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: '',
    },

    fullScreen: {
      type: Boolean,
      default: false,
    },

    closeFixed: {
      type: Boolean,
      default: false,
    },

    noClose: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      opened: false, // состояние после открытия (кейс с самозакрытием на click-outside)
      clickOutsideConfig: {
        handler: this.closeByOutside,
        events: ['touchend', 'click'],
        capture: true,
      },
    };
  },

  icons: {
    IconClose,
  },

  computed: {
    ...mapGetters({
      scrollableMenuElement: 'menu/scrollableMenuEl',
    }),
  },

  watch: {
    visible() {
      if (this.visible) {
        this.$nextTick(this.onOpen);
      } else {
        this.onClose();
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.scrollableMenuElement) return;

      const scrollableMenu = document.querySelector('.scrollable-menu');
      this.SET_SCROLLABLE_MENU(scrollableMenu);
    });
  },

  beforeUnmount() {
    this.onClose();
  },

  methods: {
    ...mapMutations({
      SET_SCROLLABLE_MENU: 'menu/setScrollableMenu',
    }),

    onOpen() {
      document?.body?.classList?.add('modal-opened');

      if (!this.backgroundMode) {
        disableBodyScroll(this.$refs.content, {
          reserveScrollBarGap: true, // добавляет отступ, вместо пропавшего скролла
        });
      }

      if (document.body.style.paddingRight) {
        this.toggleScrollBarWidthTo(this.scrollableMenuElement);
      }

      if (this.closeFixed) {
        // отступаем, чтоб крестик не заезжал на скроллбар
        const box = this.$refs.content;
        const scrollbarWidth = box.offsetWidth - box.clientWidth;

        this.$refs.closeButton.style.marginRight = `${scrollbarWidth}px`;
      }
    },

    onClose() {
      document.body.classList.remove('modal-opened');

      if (!this.backgroundMode && this.$refs?.content) {
        enableBodyScroll(this.$refs.content);
      }

      if (!document.body.style.paddingRight) {
        this.toggleScrollBarWidthTo(this.scrollableMenuElement);
      }
    },

    close({ isOutside } = {}) {
      if (this.opened) {
        this.$emit('on-close', { isOutside });
      }
    },

    closeByOutside(e) {
      if (e.target !== this.$refs.modal) return; // фикс исправляющий неожиданное закрытие модалок
      // при их наложении друг на друга
      if (!this.backgroundMode) {
        e.preventDefault();
        this.close({ isOutside: true });
      }
    },

    toggleScrollBarWidthTo(el) {
      if (!el) return;

      return el.style.setProperty('--scroll-bar-width', document.body.style.paddingRight);
    },

    onAfterEnter() {
      this.opened = true;
      this.$emit('after-enter');
    },

    onLeave() {
      this.opened = false;
    },

    onEnter() {
      this.$emit('enter');
    },
  },
};
</script>

<style lang="scss">
.modal {
  @include stacking("popup");
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  background: rgba(0, 0, 0, .5);
  overflow: hidden;

  &-content {
    @include stacking("popup");
    width: 100%;
    position: absolute;
    bottom: 0;
    padding: 24px;
    background-color: $white;
    border-radius: 4px 4px 0 0;
    max-height: 96%;
    overflow-y: auto;
  }

  .modal_bottom-sheet-mode &-content {
    width: 100%;
    position: absolute;
    bottom: 0;
    padding: 12px 16px 16px;
    background-color: $white;
    border-radius: 4px 4px 0 0;
    max-height: 96%;
    overflow-y: auto;
  }

  .modal_full-screen &-content {
    width: 100%;
    min-height: 100%;
    position: absolute;
    bottom: 0;
    padding: 0;
    background-color: $white;
    max-height: 100%;
    overflow-y: auto;
    border-radius: 0;
  }

  &__title {
    @include stacking("popup");
    font-family: Gilroy, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    text-align: center;
  }

  .close-button {
    position: absolute;
    top: 0;
    right: 0;
    padding: 14px;
    width: 48px;
    height: 48px;
    color: $gray_lu;
    cursor: pointer;

    &_fixed {
      position: fixed;
    }
  }

  // transition
  &-enter-active,
  &-leave-active {
    transition: opacity .2s;
    .modal-content {
      transition: transform .26s;
    }
  }
  &-enter,
  &-leave-active {
    opacity: 0;
    .modal-content {
      transform: translateY(100px);
    }
  }
}

@include breakpoint("tablet") {
  .modal {
    align-items: center;
    &.scrollable {
      justify-content: flex-start;
      overflow: auto;

      .modal-content {
        margin-top: 40px;
      }
    }
    &-content {
      bottom: auto;
      width: initial;
      border-radius: 4px;
      max-height: none;
    }

    // transition
    &-enter-active,
    &-leave-active {
      transition: opacity .2s;
      .modal-content {
        transition: transform .15s;
      }
    }
    &-enter,
    &-leave-active {
      opacity: 0;
      .modal-content {
        transform: scale(.94);
      }
    }
  }
}
</style>
